import { useMemo } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '~/lib/utils'
import { isDevelopment } from '~/utils/misc'

import MarkDown from '~/components/Helpers/MarkDown'

type AlertProps = {
  content: {
    type: VariantProps<typeof alertVariants>['type']
    heading?: string
    copy: string
    icon?: Image
    showIcon?: boolean
  }
  className?: string
}

const alertVariants = cva(
  'relative flex text-left sm:gap-xs gap-2xs-plus rounded-none! border-none w-full !p-xs sm:!p-xs-plus text-blue-d max-w-[80ch]',
  {
    variants: {
      type: {
        general: '!bg-grey-l',
        success: '!bg-green-40',
        info: '!bg-blue-l-40',
        warning: '!bg-orange-40',
        error: '!bg-pink-40'
      }
    },
    defaultVariants: {
      type: 'general'
    }
  }
)

const Alert = ({ content, className }: AlertProps) => {
  const { type, heading, copy, icon, showIcon = true } = content

  const previewUrl =
    isDevelopment && !icon?.asset.sys.uri.startsWith('https')
      ? 'https://preview-websitemodern-uni-hull.cloud.contensis.com'
      : ''

  const iconType = useMemo(() => {
    switch (type) {
      case 'general':
        return {
          image: icon?.asset.sys.uri,
          altText: icon?.altText
        }
      case 'success':
        return {
          image: '/site-elements/icons/single-colour/ink-blue/check.svg',
          altText: 'Checkmark'
        }
      case 'info':
        return {
          image: '/site-elements/icons/single-colour/ink-blue/circle-info.svg',
          altText: 'Information'
        }
      case 'warning':
        return {
          image:
            '/site-elements/icons/single-colour/ink-blue/triangle-warning.svg',
          altText: 'Warning'
        }
      case 'error':
        return {
          image:
            '/site-elements/icons/single-colour/ink-blue/circle-warning.svg',
          altText: 'Error'
        }

      default:
        return null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  return (
    <div role='alert' className={cn(alertVariants({ type }), className)}>
      {showIcon && iconType && (
        <div className='shrink-0'>
          <img
            className='w-xs-plus h-xs-plus sm:h-[28px] sm:w-[28px]'
            src={`${previewUrl}${iconType.image}`}
            alt=''
            role='presentation'
          />
        </div>
      )}
      <div className='space-y-xs'>
        <h5 className='text-base-lg sm:text-h4-md lg:text-h4-lg font-bold'>
          {heading}
        </h5>
        <MarkDown
          className='sm:text-base-md space-y-xs lg:text-base-lg'
          content={copy}
        />
      </div>
    </div>
  )
}

export default Alert
